<template>
    <div>
        <!-- Loading State -->
        <div class="study-plan-content-frame" v-if="loading">
            <div class="has-text-centered py-6">
                <i class="fas fa-spin fa-spinner fa-2x"></i>
                <p class="mt-4">Loading study plan...</p>
            </div>
        </div>

        <!-- Pending Approval State -->
        <template v-else-if="setupStatus === 'submitted'">
            <div class="study-plan-content-frame">
                <div class="has-text-centered py-6">
                    <i class="fas fa-clock fa-4x has-text-warning mb-4"></i>
                    <h2 class="mb-3">Course Study Plan Pending Approval</h2>
                    <p class="has-text-grey mb-4" style="max-width: 500px; margin: 0 auto;">
                        Your course study plan has been submitted and is awaiting administrator approval.
                        You will receive an email notification once your plan has been reviewed.
                    </p>
                    <p class="is-size-7 has-text-grey mt-5" v-if="setupData.submitted_at">
                        Submitted: {{ formatDate(setupData.submitted_at) }}
                    </p>
                </div>
            </div>
        </template>

        <!-- Approved State -->
        <template v-else-if="setupStatus === 'approved'">
            <div class="study-plan-content-frame approved-content">
                <!-- Tabs for Syllabus and Plan -->
                <b-tabs v-model="approvedTab" type="is-boxed" class="mb-4">
                    <b-tab-item label="Your Syllabus" icon="list-alt">
                        <div v-if="setupData.syllabus" class="ai-content" v-html="setupData.syllabus"></div>
                        <div v-else class="has-text-centered has-text-grey py-5">
                            <i class="fas fa-spinner fa-spin fa-2x mb-3"></i>
                            <p>Your personalized syllabus is being generated...</p>
                        </div>
                    </b-tab-item>
                    <b-tab-item label="Your Plan" icon="clipboard-list">
                        <div class="plan-summary">
                            <div class="mb-5 pb-4" style="border-bottom: 1px solid #eee;">
                                <span class="tag is-success is-light">
                                    <i class="fas fa-check-circle mr-2"></i>Approved
                                </span>
                                <span class="is-size-7 has-text-grey ml-3" v-if="setupData.approved_at">
                                    {{ formatDate(setupData.approved_at) }}
                                </span>
                            </div>
                            <h5 class="mb-3">Goals</h5>
                            <ul class="mb-4">
                                <li v-for="(goal, i) in allGoals" :key="'goal-'+i">{{ goal }}</li>
                            </ul>
                            <h5 class="mb-3">Objectives & Hours</h5>
                            <ul class="mb-4">
                                <li v-for="(obj, i) in allObjectivesWithText" :key="'obj-'+i">
                                    {{ obj.text }} <span class="tag is-info is-light ml-2">{{ setupData.hour_allocations[obj.id] || 0 }} hrs</span>
                                </li>
                            </ul>
                            <h5 class="mb-3">Evaluation Plan</h5>
                            <p>{{ setupData.evaluation_plan }}</p>
                        </div>
                    </b-tab-item>
                </b-tabs>

            </div>
        </template>

        <!-- Wizard Form -->
        <template v-else>
            <div class="study-plan-content-frame">
                <!-- Header -->
                <div class="study-plan-header">
                    <img v-if="enrollment && enrollment.course && enrollment.course.logo" :src="enrollment.course.logo" style="height: 70px;"/>

                    <div class="mt-4 mb-4">
                        <div class="has-text-grey-dark">
                            <strong class="has-text-grey-dark">Develop Your Course Study Plan</strong> -
                            Step {{ activeStep + 1 }} of 4
                        </div>
                        <h2 class="mt-2">{{ stepTitles[activeStep] }}</h2>
                    </div>

                    <!-- Progress bar -->
                    <div class="mb-4">
                        <b-progress :value="((activeStep + 1) / 4) * 100" type="is-info" size="is-small"></b-progress>
                    </div>
                </div>

                <!-- Admin feedback message for changes_requested -->
                <b-message v-if="setupStatus === 'changes_requested' && setupData.admin_feedback" type="is-warning" class="mb-5">
                    <div class="columns is-gapless">
                        <div class="column is-narrow"><i class="fas fa-triangle-exclamation mr-3"></i></div>
                        <div class="column">
                            <strong>Changes Requested</strong>
                            <p class="mt-2">{{ setupData.admin_feedback }}</p>
                        </div>
                    </div>
                </b-message>

                <!-- Step Content -->
                <div class="step-content">
                    <!-- Step 1: Resume -->
                    <div v-show="activeStep === 0">
                        <p class="mb-5 has-text-grey">Tell us about yourself and why this course is relevant to you.</p>

                        <b-field label="Professional Background" message="Describe your current role and professional experience (minimum 50 characters)">
                            <b-input
                                type="textarea"
                                v-model="setupData.resume.background"
                                placeholder="Describe your current position, years of experience, and relevant background..."
                                rows="4"
                            ></b-input>
                        </b-field>
                        <div class="is-size-7 has-text-right mb-4" :class="{'has-text-danger': charCount('resume.background') < 50, 'has-text-success': charCount('resume.background') >= 50}">
                            {{ charCount('resume.background') }} / 50 min characters
                        </div>

                        <b-field label="Work History" message="Describe your relevant work history and experience (minimum 50 characters)">
                            <b-input
                                type="textarea"
                                v-model="setupData.resume.work_history"
                                placeholder="List previous relevant positions and experiences..."
                                rows="4"
                            ></b-input>
                        </b-field>
                        <div class="is-size-7 has-text-right mb-4" :class="{'has-text-danger': charCount('resume.work_history') < 50, 'has-text-success': charCount('resume.work_history') >= 50}">
                            {{ charCount('resume.work_history') }} / 50 min characters
                        </div>

                        <b-field label="Why This Subject Matters" message="Explain why this subject is important to your professional development (minimum 50 characters)">
                            <b-input
                                type="textarea"
                                v-model="setupData.resume.importance"
                                placeholder="Explain how this course will benefit your professional growth..."
                                rows="4"
                            ></b-input>
                        </b-field>
                        <div class="is-size-7 has-text-right mb-4" :class="{'has-text-danger': charCount('resume.importance') < 50, 'has-text-success': charCount('resume.importance') >= 50}">
                            {{ charCount('resume.importance') }} / 50 min characters
                        </div>
                    </div>

                    <!-- Step 2: Overview -->
                    <div v-show="activeStep === 1">
                        <p class="mb-5 has-text-grey">Select your goals, objectives, and application level for this course.</p>

                        <!-- Course Info -->
                        <div class="box mb-5" style="background-color: #f8f9fa;">
                            <div class="has-text-weight-bold">{{ course.title }}</div>
                            <div class="is-size-7 has-text-grey mt-1" v-if="course.description">{{ course.description }}</div>
                        </div>

                        <!-- Goals -->
                        <div class="mb-5">
                            <label class="label">Goals <span class="has-text-danger">*</span></label>
                            <p class="is-size-7 has-text-grey mb-3">Select at least {{ minGoals }} goals for this course</p>

                            <div v-if="courseGoals.length > 0" class="mb-3">
                                <div v-for="(goal, index) in courseGoals" :key="'goal-' + index" class="mb-2">
                                    <b-checkbox v-model="setupData.selected_goals" :native-value="goal.id || goal">
                                        {{ goal.text || goal }}
                                    </b-checkbox>
                                </div>
                            </div>
                            <div v-else class="has-text-grey is-size-7 mb-3">No predefined goals available</div>

                            <!-- Custom goals -->
                            <div class="mt-4">
                                <label class="label is-size-7">Custom Goals</label>
                                <div v-for="(goal, index) in setupData.custom_goals" :key="'custom-goal-' + index" class="mb-2">
                                    <div class="columns is-gapless is-mobile">
                                        <div class="column">
                                            <b-input v-model="setupData.custom_goals[index]" size="is-small"></b-input>
                                        </div>
                                        <div class="column is-narrow ml-2">
                                            <b-button type="is-danger" size="is-small" icon-left="times" @click="removeCustomGoal(index)"></b-button>
                                        </div>
                                    </div>
                                </div>
                                <b-button type="is-light" size="is-small" icon-left="plus" @click="addCustomGoal">Add Custom Goal</b-button>
                            </div>
                            <div class="is-size-7 mt-2" :class="{'has-text-danger': totalGoals < minGoals, 'has-text-success': totalGoals >= minGoals}">
                                {{ totalGoals }} / {{ minGoals }} goals selected
                            </div>
                        </div>

                        <!-- Objectives -->
                        <div class="mb-5">
                            <label class="label">Objectives <span class="has-text-danger">*</span></label>
                            <p class="is-size-7 has-text-grey mb-3">Select at least {{ minObjectives }} objectives for this course</p>

                            <div v-if="courseObjectives.length > 0" class="mb-3">
                                <div v-for="(objective, index) in courseObjectives" :key="'obj-' + index" class="mb-2">
                                    <b-checkbox v-model="setupData.selected_objectives" :native-value="objective.id || objective">
                                        {{ objective.text || objective }}
                                    </b-checkbox>
                                </div>
                            </div>
                            <div v-else class="has-text-grey is-size-7 mb-3">No predefined objectives available</div>

                            <!-- Custom objectives -->
                            <div class="mt-4">
                                <label class="label is-size-7">Custom Objectives</label>
                                <div v-for="(obj, index) in setupData.custom_objectives" :key="'custom-obj-' + index" class="mb-2">
                                    <div class="columns is-gapless is-mobile">
                                        <div class="column">
                                            <b-input v-model="setupData.custom_objectives[index]" size="is-small"></b-input>
                                        </div>
                                        <div class="column is-narrow ml-2">
                                            <b-button type="is-danger" size="is-small" icon-left="times" @click="removeCustomObjective(index)"></b-button>
                                        </div>
                                    </div>
                                </div>
                                <b-button type="is-light" size="is-small" icon-left="plus" @click="addCustomObjective">Add Custom Objective</b-button>
                            </div>
                            <div class="is-size-7 mt-2" :class="{'has-text-danger': totalObjectives < minObjectives, 'has-text-success': totalObjectives >= minObjectives}">
                                {{ totalObjectives }} / {{ minObjectives }} objectives selected
                            </div>
                        </div>

                        <!-- Application Level -->
                        <div class="mb-5">
                            <label class="label">Application Level <span class="has-text-danger">*</span></label>
                            <p class="is-size-7 has-text-grey mb-3">Select the level(s) at which you will apply this learning</p>

                            <div v-for="level in applicationLevels" :key="level.value" class="mb-2">
                                <b-checkbox v-model="setupData.application_level" :native-value="level.value">
                                    <strong>{{ level.label }}</strong> - {{ level.description }}
                                </b-checkbox>
                            </div>
                            <div class="is-size-7 mt-2" :class="{'has-text-danger': !setupData.application_level.length, 'has-text-success': setupData.application_level.length > 0}">
                                {{ setupData.application_level.length }} level(s) selected
                            </div>
                        </div>
                    </div>

                    <!-- Step 3: Agenda -->
                    <div v-show="activeStep === 2">
                        <p class="mb-5 has-text-grey">Allocate your enrolled hours across your selected objectives.</p>

                        <!-- Hours summary -->
                        <div class="box mb-5" style="background-color: #f8f9fa;">
                            <div class="columns is-mobile">
                                <div class="column has-text-centered">
                                    <div class="is-size-3 has-text-weight-bold has-text-primary">{{ enrolledHours }}</div>
                                    <div class="is-size-7 has-text-grey">Enrolled Hours</div>
                                </div>
                                <div class="column has-text-centered">
                                    <div class="is-size-3 has-text-weight-bold" :class="{'has-text-success': allocatedHours === enrolledHours, 'has-text-warning': allocatedHours !== enrolledHours}">{{ allocatedHours }}</div>
                                    <div class="is-size-7 has-text-grey">Allocated Hours</div>
                                </div>
                                <div class="column has-text-centered">
                                    <div class="is-size-3 has-text-weight-bold" :class="{'has-text-success': remainingHours === 0, 'has-text-danger': remainingHours !== 0}">{{ remainingHours }}</div>
                                    <div class="is-size-7 has-text-grey">Remaining</div>
                                </div>
                            </div>
                        </div>

                        <div v-if="allObjectivesWithText.length === 0" class="notification is-warning">
                            <i class="fas fa-exclamation-triangle mr-2"></i>
                            Please select objectives in the previous step first.
                        </div>

                        <div v-else>
                            <div v-for="(objective, index) in allObjectivesWithText" :key="'alloc-' + index" class="mb-5">
                                <label class="label is-size-6">{{ objective.text }}</label>
                                <div class="columns is-mobile is-vcentered">
                                    <div class="column">
                                        <b-slider
                                            v-model="setupData.hour_allocations[objective.id]"
                                            :min="0"
                                            :max="enrolledHours"
                                            :step="0.5"
                                            type="is-info"
                                        ></b-slider>
                                    </div>
                                    <div class="column is-narrow" style="width: 100px;">
                                        <b-input
                                            type="number"
                                            v-model.number="setupData.hour_allocations[objective.id]"
                                            :min="0"
                                            :max="enrolledHours"
                                            step="0.5"
                                            size="is-small"
                                        ></b-input>
                                    </div>
                                    <div class="column is-narrow">
                                        <span class="is-size-7 has-text-grey">hours</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <b-message v-if="remainingHours !== 0 && allObjectivesWithText.length > 0" type="is-warning">
                            <i class="fas fa-exclamation-triangle mr-2"></i>
                            Total allocated hours must equal enrolled hours ({{ enrolledHours }}).
                            <span v-if="remainingHours > 0">You have {{ remainingHours }} hours remaining to allocate.</span>
                            <span v-else>You have allocated {{ Math.abs(remainingHours) }} hours too many.</span>
                        </b-message>
                    </div>

                    <!-- Step 4: Specifics -->
                    <div v-show="activeStep === 3">
                        <p class="mb-5 has-text-grey">Review materials, resources, and finalize your study plan.</p>

                        <!-- Materials (display only) -->
                        <div class="mb-5" v-if="courseMaterials.length > 0">
                            <label class="label">Required Materials</label>
                            <ul class="ml-4">
                                <li v-for="(material, index) in courseMaterials" :key="'mat-' + index">{{ material.text || material.title || material }}</li>
                            </ul>
                        </div>

                        <!-- Resources (display only) -->
                        <div class="mb-5" v-if="courseResources.length > 0">
                            <label class="label">Resources</label>
                            <ul class="ml-4">
                                <li v-for="(resource, index) in courseResources" :key="'res-' + index">
                                    <a v-if="resource.url" :href="resource.url" target="_blank">{{ resource.title }}</a>
                                    <span v-else>{{ resource.title || resource }}</span>
                                </li>
                            </ul>
                        </div>

                        <!-- Other Materials -->
                        <b-field label="Additional Materials (Optional)" message="List any additional materials you plan to use">
                            <b-input
                                type="textarea"
                                v-model="setupData.other_materials"
                                placeholder="List any additional books, websites, or resources you plan to use..."
                                rows="3"
                            ></b-input>
                        </b-field>

                        <!-- Evaluation Plan -->
                        <b-field label="Evaluation Plan" message="Describe how you will evaluate your learning (minimum 50 characters)">
                            <b-input
                                type="textarea"
                                v-model="setupData.evaluation_plan"
                                placeholder="Describe how you will measure your progress and success in this course..."
                                rows="4"
                            ></b-input>
                        </b-field>
                        <div class="is-size-7 has-text-right mb-4" :class="{'has-text-danger': charCount('evaluation_plan') < 50, 'has-text-success': charCount('evaluation_plan') >= 50}">
                            {{ charCount('evaluation_plan') }} / 50 min characters
                        </div>

                        <!-- Custom Course Name (if enabled) -->
                        <div v-if="courseConfig.custom_naming" class="mb-5">
                            <b-field label="Custom Course Name (Optional)">
                                <b-input
                                    v-model="setupData.custom_course_name"
                                    placeholder="Enter a custom name for this course..."
                                ></b-input>
                            </b-field>
                        </div>

                        <!-- Reflections Agreement -->
                        <div class="box mt-5" style="background-color: #fff8e6; border: 1px solid #ffc107;">
                            <b-checkbox v-model="setupData.reflections_agreed">
                                <strong>I agree to submit reflections</strong>
                                <p class="is-size-7 has-text-grey mt-1">
                                    I understand that I will be required to submit written reflections as part of this course to demonstrate my learning and progress.
                                </p>
                            </b-checkbox>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Bottom Controls -->
            <div class="content-controls">
                <div class="columns">
                    <div class="column">
                        <b-button type="is-light" size="is-small" @click="saveDraft" :loading="saving" :disabled="saving">
                            <i class="fas fa-save mr-2"></i>Save Draft
                        </b-button>
                    </div>
                    <div class="column has-text-right">
                        <b-button type="is-white" outlined v-if="activeStep > 0" @click="prevStep" :disabled="saving">
                            <i class="fas fa-arrow-left mr-3"></i>Previous
                        </b-button>
                        <b-button
                            type="is-white"
                            outlined
                            class="ml-3"
                            v-if="activeStep < 3"
                            @click="nextStep"
                            :disabled="!canProceed || saving"
                            :loading="saving"
                        >
                            Next Step<i class="fas fa-arrow-right ml-3"></i>
                        </b-button>
                        <b-button
                            type="is-success"
                            class="ml-3"
                            v-if="activeStep === 3"
                            @click="submitSetup"
                            :disabled="!canSubmit || saving"
                            :loading="saving"
                        >
                            Submit for Approval<i class="fas fa-paper-plane ml-3"></i>
                        </b-button>
                    </div>
                </div>
            </div>
        </template>
    </div>
</template>

<script>
import {client as http} from "../../http_client";
import {mapGetters} from "vuex";
import moment from "moment";

export default {
    props: ['enrollmentId'],
    data() {
        return {
            loading: true,
            saving: false,
            activeStep: 0,
            approvedTab: 0,
            course: {},
            courseConfig: {},
            maxHours: 0,
            setupData: {
                resume: {
                    background: '',
                    work_history: '',
                    importance: ''
                },
                selected_goals: [],
                custom_goals: [],
                selected_objectives: [],
                custom_objectives: [],
                application_level: [],
                hour_allocations: {},
                other_materials: '',
                evaluation_plan: '',
                custom_course_name: '',
                reflections_agreed: false,
                status: 'draft'
            },
            stepTitles: [
                'Your Background',
                'Goals & Objectives',
                'Hour Allocation',
                'Finalize Your Plan'
            ],
            applicationLevels: [
                {value: 'awareness', label: 'Awareness', description: 'Basic understanding of concepts'},
                {value: 'beginning', label: 'Beginning', description: 'Initial application of skills'},
                {value: 'developing', label: 'Developing', description: 'Growing proficiency'},
                {value: 'applying', label: 'Applying', description: 'Regular use in practice'},
                {value: 'integrating', label: 'Integrating', description: 'Seamless incorporation into work'}
            ]
        };
    },
    computed: {
        ...mapGetters(['enrollment']),
        setupStatus() {
            return this.enrollment?.setup_status || null;
        },
        courseGoals() {
            if (!this.courseConfig.goals) return [];
            let goals = this.courseConfig.goals;
            // Parse if it's a JSON string
            if (typeof goals === 'string') {
                try {
                    goals = JSON.parse(goals);
                } catch (e) {
                    // Not JSON, treat as newline-separated
                    return goals.split('\n').filter(g => g.trim());
                }
            }
            return Array.isArray(goals) ? goals : [];
        },
        courseObjectives() {
            if (!this.courseConfig.objectives) return [];
            let objectives = this.courseConfig.objectives;
            // Parse if it's a JSON string
            if (typeof objectives === 'string') {
                try {
                    objectives = JSON.parse(objectives);
                } catch (e) {
                    // Not JSON, treat as newline-separated
                    return objectives.split('\n').filter(o => o.trim());
                }
            }
            return Array.isArray(objectives) ? objectives : [];
        },
        courseMaterials() {
            if (!this.courseConfig.materials) return [];
            let materials = this.courseConfig.materials;
            // Parse if it's a JSON string
            if (typeof materials === 'string') {
                try {
                    materials = JSON.parse(materials);
                } catch (e) {
                    // Not JSON, treat as newline-separated
                    return materials.split('\n').filter(m => m.trim());
                }
            }
            return Array.isArray(materials) ? materials : [];
        },
        courseResources() {
            if (!this.courseConfig.resources) return [];
            let resources = this.courseConfig.resources;
            // Parse if it's a JSON string
            if (typeof resources === 'string') {
                try {
                    resources = JSON.parse(resources);
                } catch (e) {
                    return [];
                }
            }
            return Array.isArray(resources) ? resources : [];
        },
        minGoals() {
            return this.courseConfig.min_goals || 2;
        },
        minObjectives() {
            return this.courseConfig.min_objectives || 2;
        },
        totalGoals() {
            return this.setupData.selected_goals.length + this.setupData.custom_goals.filter(g => g.trim()).length;
        },
        allGoals() {
            // Map selected goal IDs to text
            const selected = this.setupData.selected_goals.map(id => {
                const goal = this.courseGoals.find(g => (g.id || g) === id);
                return goal?.text || goal || id;
            });
            // Add custom goals
            const custom = this.setupData.custom_goals.filter(g => g.trim());
            return [...selected, ...custom];
        },
        totalObjectives() {
            return this.setupData.selected_objectives.length + this.setupData.custom_objectives.filter(o => o.trim()).length;
        },
        allObjectives() {
            const selected = [...this.setupData.selected_objectives];
            const custom = this.setupData.custom_objectives.filter(o => o.trim());
            return [...selected, ...custom];
        },
        allObjectivesWithText() {
            // Map selected objective IDs to objects with id and text
            const selected = this.setupData.selected_objectives.map(id => {
                const obj = this.courseObjectives.find(o => (o.id || o) === id);
                return {
                    id: id,
                    text: obj?.text || obj || id
                };
            });
            // Map custom objectives to objects with id and text
            const custom = this.setupData.custom_objectives
                .filter(o => o.trim())
                .map((text, index) => ({
                    id: 'custom_' + index,
                    text: text
                }));
            return [...selected, ...custom];
        },
        enrolledHours() {
            // Use max hours from linked course config, fall back to course config setting
            return this.maxHours || this.courseConfig.enrolled_hours || 20;
        },
        allocatedHours() {
            return Object.values(this.setupData.hour_allocations).reduce((sum, hours) => sum + (hours || 0), 0);
        },
        remainingHours() {
            return this.enrolledHours - this.allocatedHours;
        },
        canProceed() {
            switch (this.activeStep) {
                case 0: // Resume
                    return this.charCount('resume.background') >= 50 &&
                           this.charCount('resume.work_history') >= 50 &&
                           this.charCount('resume.importance') >= 50;
                case 1: // Overview
                    return this.totalGoals >= this.minGoals &&
                           this.totalObjectives >= this.minObjectives &&
                           this.setupData.application_level.length > 0;
                case 2: // Agenda
                    return this.remainingHours === 0 &&
                           this.allObjectivesWithText.length > 0 &&
                           this.allObjectivesWithText.every(obj => this.setupData.hour_allocations[obj.id] > 0);
                default:
                    return true;
            }
        },
        canSubmit() {
            return this.canProceed &&
                   this.charCount('evaluation_plan') >= 50 &&
                   this.setupData.reflections_agreed;
        }
    },
    methods: {
        charCount(path) {
            const parts = path.split('.');
            let value = this.setupData;
            for (const part of parts) {
                value = value?.[part];
            }
            return (value || '').length;
        },
        addCustomGoal() {
            this.setupData.custom_goals.push('');
        },
        removeCustomGoal(index) {
            this.setupData.custom_goals.splice(index, 1);
        },
        addCustomObjective() {
            this.setupData.custom_objectives.push('');
        },
        removeCustomObjective(index) {
            const key = 'custom_' + index;
            delete this.setupData.hour_allocations[key];
            this.setupData.custom_objectives.splice(index, 1);
        },
        initializeHourAllocations() {
            this.allObjectivesWithText.forEach((obj) => {
                if (this.setupData.hour_allocations[obj.id] === undefined) {
                    this.$set(this.setupData.hour_allocations, obj.id, 0);
                }
            });
        },
        async load() {
            this.loading = true;
            try {
                const response = await http.get(`app/enrollments/${this.enrollmentId}/setup`, {force: true});
                this.course = response.data.course || {};
                this.maxHours = response.data.max_hours || 0;
                let config = response.data.course?.self_directed_config || {};
                // Parse if it's a JSON string
                if (typeof config === 'string') {
                    try {
                        config = JSON.parse(config);
                    } catch (e) {
                        console.error('Error parsing self_directed_config:', e);
                        config = {};
                    }
                }
                this.courseConfig = config;

                if (response.data.setup_data) {
                    const loaded = response.data.setup_data;
                    this.setupData = {
                        ...this.setupData,
                        ...loaded,
                        resume: {...this.setupData.resume, ...(loaded.resume || {})},
                        selected_goals: loaded.selected_goals || [],
                        custom_goals: loaded.custom_goals || [],
                        selected_objectives: loaded.selected_objectives || [],
                        custom_objectives: loaded.custom_objectives || [],
                        application_level: loaded.application_level || [],
                        hour_allocations: loaded.hour_allocations || {}
                    };
                }

                this.initializeHourAllocations();
            } catch (err) {
                console.error('Error loading course study plan:', err);
                this.$buefy.toast.open({
                    message: 'Error loading course study plan',
                    type: 'is-danger'
                });
            } finally {
                this.loading = false;
            }
        },
        async saveDraft() {
            this.saving = true;
            try {
                // Clean up hour_allocations to only include current objectives
                const validObjectiveIds = this.allObjectivesWithText.map(o => o.id);
                const cleanedHourAllocations = {};
                for (const id of validObjectiveIds) {
                    if (this.setupData.hour_allocations[id] !== undefined) {
                        cleanedHourAllocations[id] = this.setupData.hour_allocations[id];
                    }
                }

                const saveData = {
                    ...this.setupData,
                    hour_allocations: cleanedHourAllocations
                };

                await http.post(`app/enrollments/${this.enrollmentId}/setup`, {
                    setup_data: saveData,
                    submit: false
                }, {force: true});

                this.$buefy.toast.open({
                    message: 'Draft saved',
                    type: 'is-success'
                });
            } catch (err) {
                console.error('Error saving draft:', err);
                this.$buefy.toast.open({
                    message: 'Error saving draft',
                    type: 'is-danger'
                });
            } finally {
                this.saving = false;
            }
        },
        async nextStep() {
            await this.saveDraft();
            if (this.activeStep === 1) {
                this.initializeHourAllocations();
            }
            this.activeStep++;
            window.scrollTo(0, 0);
        },
        prevStep() {
            this.activeStep--;
            window.scrollTo(0, 0);
        },
        async submitSetup() {
            if (!this.canSubmit) {
                this.$buefy.toast.open({
                    message: 'Please complete all required fields',
                    type: 'is-warning'
                });
                return;
            }

            this.saving = true;
            try {
                // Clean up hour_allocations to only include current objectives
                const validObjectiveIds = this.allObjectivesWithText.map(o => o.id);
                const cleanedHourAllocations = {};
                for (const id of validObjectiveIds) {
                    cleanedHourAllocations[id] = this.setupData.hour_allocations[id] || 0;
                }

                const submitData = {
                    ...this.setupData,
                    hour_allocations: cleanedHourAllocations
                };

                const response = await http.post(`app/enrollments/${this.enrollmentId}/setup`, {
                    setup_data: submitData,
                    submit: true
                }, {force: true});

                this.setupData = response.data.setup_data;

                this.$buefy.toast.open({
                    message: 'Course study plan submitted for approval',
                    type: 'is-success',
                    duration: 5000
                });

                this.$emit('submitted');
            } catch (err) {
                console.error('Error submitting course study plan:', err);
                const errors = err.response?.data?.errors || [];
                const message = errors.length > 0 ? errors.join(', ') : 'Error submitting course study plan';
                this.$buefy.toast.open({
                    message,
                    type: 'is-danger',
                    duration: 5000
                });
            } finally {
                this.saving = false;
            }
        },
        formatDate(date) {
            if (!date) return '';
            return moment(date).format('MMMM Do, YYYY [at] h:mm A');
        }
    },
    watch: {
        'setupData.selected_objectives': {
            handler() {
                this.initializeHourAllocations();
            },
            deep: true
        },
        'setupData.custom_objectives': {
            handler() {
                this.initializeHourAllocations();
            },
            deep: true
        }
    },
    mounted() {
        this.load();
    }
};
</script>

<style lang="scss" scoped>
.study-plan-content-frame {
    position: absolute;
    top: 105px;
    left: 5rem;
    right: 4rem;
    bottom: 7rem;
    background-color: #fff;
    border-radius: 8px;
    box-shadow: 5px 5px 30px 0 rgba(0, 0, 0, 0.3);
    padding: 2rem 2rem 7rem;

    overflow-x: hidden;
    overflow-y: auto;
}

.study-plan-header {
    margin-bottom: 1.5rem;
    border: none;

    h2 {
        margin-top: 0.5rem;
        margin-bottom: 1rem;
        border: none;
        border-bottom: none;
    }

    > div {
        border: none;
    }

    ::v-deep .progress-wrapper {
        border: none;
    }
}

.step-content {
    padding-bottom: 2rem;
}

.content-controls {
    position: absolute;
    left: 5rem;
    right: 4rem;
    bottom: 1.5rem;
    padding: 3rem 0 1rem;
    font-size: 0.85rem;

    color: white;

    strong {
        color: white;
    }
}

.box {
    border-radius: 8px;
}

.approved-content {
    padding-bottom: 2rem;

    .ai-content {
        line-height: 1.7;

        ::v-deep h4 {
            color: #363636;
            font-weight: 600;
            margin-top: 1.5rem;
            margin-bottom: 0.75rem;
        }

        ::v-deep h5 {
            color: #4a4a4a;
            font-weight: 600;
            margin-top: 1.25rem;
            margin-bottom: 0.5rem;
        }

        ::v-deep ul, ::v-deep ol {
            margin-left: 1.5rem;
            margin-bottom: 1rem;
        }

        ::v-deep li {
            margin-bottom: 0.5rem;
        }

        ::v-deep p {
            margin-bottom: 1rem;
        }
    }

    .plan-summary {
        h5 {
            color: #363636;
            font-weight: 600;
            border-bottom: 1px solid #eee;
            padding-bottom: 0.5rem;
        }

        ul {
            margin-left: 1.5rem;
        }

        li {
            margin-bottom: 0.5rem;
        }
    }
}
</style>
